import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { CheckCircle, Calendar, MapPin, User, Building, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_8c6a1162-6fec-4154-8fe3-0b918a141fc6/artifacts/ojlspj1s_Untitled%20design.png';

const RATING_OPTIONS = [
  { value: 1, label: 'Poor', labelAr: 'ضعيف' },
  { value: 2, label: 'Below Avg', labelAr: 'أقل من المتوسط' },
  { value: 3, label: 'Average', labelAr: 'متوسط' },
  { value: 4, label: 'Good', labelAr: 'جيد' },
  { value: 5, label: 'Great', labelAr: 'ممتاز' },
];

const CRITERIA = [
  { key: 'participation', label: 'Participation', labelAr: 'المشاركة' },
  { key: 'attitude', label: 'Attitude', labelAr: 'الموقف' },
  { key: 'professionalism', label: 'Professionalism', labelAr: 'الاحترافية' },
  { key: 'communication_skills', label: 'Communication Skills', labelAr: 'مهارات التواصل' },
  { key: 'sense_of_responsibility', label: 'Sense of Responsibility', labelAr: 'الشعور بالمسؤولية' },
  { key: 'readiness_for_initiations', label: 'Readiness for Initiations', labelAr: 'الاستعداد للمبادرات' },
  { key: 'focus_ability', label: 'Focus Ability', labelAr: 'القدرة على التركيز' },
  { key: 'openness_to_feedback', label: 'Openness to Feedback', labelAr: 'الانفتاح على التغذية الراجعة' },
  { key: 'listening_skills', label: 'Listening Skills', labelAr: 'مهارات الاستماع' },
  { key: 'enthusiasm', label: 'Enthusiasm', labelAr: 'الحماس' },
];

export default function TrainerEvaluationPage() {
  const { enrollmentId } = useParams();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [error, setError] = useState(null);
  
  const [form, setForm] = useState({
    trainer_name: '',
    participation: 4,
    attitude: 4,
    professionalism: 4,
    communication_skills: 4,
    sense_of_responsibility: 4,
    readiness_for_initiations: 4,
    focus_ability: 4,
    openness_to_feedback: 4,
    listening_skills: 4,
    enthusiasm: 4,
    comments: ''
  });

  useEffect(() => {
    fetchEnrollment();
  }, [enrollmentId]);

  const fetchEnrollment = async () => {
    try {
      const response = await fetch(`${API}/trainer-evaluation/${enrollmentId}`);
      if (response.ok) {
        const data = await response.json();
        setEnrollment(data);
        if (data.trainer_eval_completed) {
          setSubmitted(true);
        }
      } else {
        setError('Enrollment not found or link is invalid.');
      }
    } catch (err) {
      setError('Failed to load enrollment details.');
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = (criterion, value) => {
    setForm(prev => ({ ...prev, [criterion]: parseInt(value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.trainer_name.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال اسم المدرب' : 'Please enter your name as trainer');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const payload = {
        enrollment_id: enrollmentId,
        ...form
      };
      
      const response = await fetch(`${API}/trainer-evaluation/${enrollmentId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast.success(language === 'ar' ? 'تم إرسال التقييم بنجاح' : 'Evaluation submitted successfully');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to submit evaluation');
      }
    } catch (err) {
      toast.error('Failed to submit evaluation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33AADD]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-xl font-semibold text-[#111827] mb-2">Link Invalid</h2>
            <p className="text-[#6B7280] mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-[#111827] mb-2">
              {language === 'ar' ? 'شكراً لك!' : 'Thank You!'}
            </h2>
            <p className="text-[#6B7280] mb-4">
              {language === 'ar' 
                ? 'تم استلام تقييم المتدرب. نشكرك على وقتك.'
                : 'Your evaluation of the participant has been received. Thank you for your time.'}
            </p>
            <div className="bg-[#F0F9FF] rounded-lg p-4 text-left">
              <p className="font-medium text-[#111827]">{enrollment.course_title}</p>
              <p className="text-sm text-[#6B7280]">
                {enrollment.city} | {enrollment.start_date} - {enrollment.end_date}
              </p>
              <p className="text-sm text-[#6B7280] mt-2">
                Participant: {enrollment.participant_name}
              </p>
            </div>
            <Link to="/" className="inline-block mt-6">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <img src={LOGO_URL} alt="WeDo HR" className="h-10" />
          </Link>
          <span className="text-sm text-[#6B7280]">Trainer Evaluation Form</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Course Context Card */}
        <Card className="mb-6 bg-gradient-to-r from-[#059669] to-[#047857] text-white">
          <CardContent className="p-6">
            <h1 className="text-2xl font-bold mb-4">
              {language === 'ar' ? enrollment.course_title_ar || enrollment.course_title : enrollment.course_title}
            </h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-white/90">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span>{language === 'ar' ? enrollment.city_ar || enrollment.city : enrollment.city}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{enrollment.start_date} - {enrollment.end_date}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{enrollment.participant_name}</span>
              </div>
              {enrollment.company && (
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  <span>{enrollment.company}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Evaluation Form */}
        <Card>
          <CardHeader>
            <CardTitle>
              {language === 'ar' ? 'تقييم المدرب للمتدرب' : 'Trainer Evaluation of Participant'}
            </CardTitle>
            <p className="text-[#6B7280] text-sm">
              {language === 'ar' 
                ? 'يرجى تقييم أداء المشارك خلال الدورة التدريبية'
                : 'Please evaluate the participant\'s performance during the training course'}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Trainer Name */}
              <div className="space-y-2">
                <Label htmlFor="trainer_name" className="text-base font-medium">
                  {language === 'ar' ? 'اسم المدرب' : 'Trainer Name'} *
                </Label>
                <Input
                  id="trainer_name"
                  value={form.trainer_name}
                  onChange={(e) => setForm(prev => ({ ...prev, trainer_name: e.target.value }))}
                  placeholder={language === 'ar' ? 'أدخل اسمك' : 'Enter your name'}
                  required
                  data-testid="trainer-name-input"
                />
              </div>

              {/* Rating Grid */}
              <div className="space-y-2">
                <Label className="text-base font-medium">
                  {language === 'ar' ? 'معايير التقييم' : 'Evaluation Criteria'}
                </Label>
                <p className="text-sm text-[#6B7280] mb-4">
                  {language === 'ar' ? 'قيّم كل معيار من 1 (ضعيف) إلى 5 (ممتاز)' : 'Rate each criterion from 1 (Poor) to 5 (Great)'}
                </p>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse" data-testid="evaluation-table">
                    <thead>
                      <tr className="bg-[#F9FAFB]">
                        <th className="text-left p-3 border font-medium text-[#374151]">
                          {language === 'ar' ? 'المعيار' : 'Criterion'}
                        </th>
                        {RATING_OPTIONS.map(opt => (
                          <th key={opt.value} className="text-center p-3 border font-medium text-[#374151] w-20">
                            {language === 'ar' ? opt.labelAr : opt.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {CRITERIA.map((criterion, idx) => (
                        <tr key={criterion.key} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'}>
                          <td className="p-3 border font-medium text-[#374151]">
                            {language === 'ar' ? criterion.labelAr : criterion.label}
                          </td>
                          {RATING_OPTIONS.map(opt => (
                            <td key={opt.value} className="text-center p-3 border">
                              <input
                                type="radio"
                                name={criterion.key}
                                value={opt.value}
                                checked={form[criterion.key] === opt.value}
                                onChange={() => handleRatingChange(criterion.key, opt.value)}
                                className="w-4 h-4 text-[#33AADD] focus:ring-[#33AADD]"
                                data-testid={`rating-${criterion.key}-${opt.value}`}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Comments */}
              <div className="space-y-2">
                <Label htmlFor="comments" className="text-base font-medium">
                  {language === 'ar' ? 'التعليقات والتوصيات' : 'Comments / Recommendations'}
                </Label>
                <Textarea
                  id="comments"
                  value={form.comments}
                  onChange={(e) => setForm(prev => ({ ...prev, comments: e.target.value }))}
                  rows={4}
                  placeholder={language === 'ar' ? 'أي تعليقات إضافية حول أداء المتدرب...' : 'Any additional comments about the participant\'s performance...'}
                  data-testid="trainer-comments"
                />
              </div>

              {/* Submit */}
              <div className="pt-4 border-t">
                <Button 
                  type="submit" 
                  className="w-full sm:w-auto bg-[#059669] hover:bg-[#047857]" 
                  disabled={submitting}
                  data-testid="trainer-eval-submit"
                >
                  {submitting 
                    ? (language === 'ar' ? 'جاري الإرسال...' : 'Submitting...') 
                    : (language === 'ar' ? 'إرسال التقييم' : 'Submit Evaluation')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
